import React, { useState } from 'react';
import {
  calculateDialogueDurationSeconds,
  countDialogueWords,
  formatVersionTimestamp,
  getSceneGenerations,
  type VideoScene,
  type VideoVersion,
} from './videoStudioTypes';
import type { StitchTransitionType } from './videoStitcher';

interface VideoScenesEditorProps {
  scenes: VideoScene[];
  versions?: VideoVersion[];
  targetDuration: number;
  onChange: (updated: VideoScene[]) => void;
  onGenerateScene?: (sceneOrder: number) => void;
  onApproveScene?: (sceneOrder: number, isApproved: boolean) => void;
  onSelectSceneGeneration?: (sceneOrder: number, videoUrl: string, durationSeconds?: number | null) => void;
  onUploadSceneVideo?: (sceneOrder: number, file: File) => void;
  onOpenTrimmer?: (scene: VideoScene) => void;
  onStitchScenes?: (transitionType?: StitchTransitionType) => void;
  generatingSceneOrder?: number | null;
  uploadingSceneOrder?: number | null;
  narratingSceneOrder?: number | null;
  onNarrateScene?: (sceneOrder: number) => void;
  isStitching?: boolean;
  stitchProgressMessage?: string | null;
}

export const VideoScenesEditor: React.FC<VideoScenesEditorProps> = ({
  scenes,
  versions = [],
  targetDuration,
  onChange,
  onGenerateScene,
  onApproveScene,
  onSelectSceneGeneration,
  onUploadSceneVideo,
  onOpenTrimmer,
  onStitchScenes,
  onNarrateScene,
  generatingSceneOrder,
  uploadingSceneOrder,
  narratingSceneOrder,
  isStitching = false,
  stitchProgressMessage = null,
}) => {
  const [transitionType, setTransitionType] = useState<StitchTransitionType>('crossfade');
  const totalDuration = scenes.reduce((acc, s) => acc + (Number(s.duration_seconds) || 0), 0);
  const readyScenesCount = scenes.filter((s) => Boolean(s.video_url)).length;

  const handleUpdate = (index: number, patch: Partial<VideoScene>) => {
    const updated = [...scenes];
    updated[index] = { ...updated[index], ...patch };
    onChange(updated);
  };

  const handleAddScene = () => {
    const newScene: VideoScene = {
      scene_order: scenes.length + 1,
      duration_seconds: 5,
      scenario: 'Cenário publicitário contemporâneo alinhado à marca',
      action: 'Ação dinâmica evidenciando a continuidade e proposta',
      camera: 'Plano médio com movimento fluido (dolly suave)',
      lighting: 'Iluminação difusa de alta definição 4K',
      dialogue: '',
      visual_prompt: 'High fidelity commercial clip, 9:16 vertical, photorealistic 4k, smooth cinematic movement',
      status: 'pending',
      video_url: null,
      reference_ids: []
    };
    onChange([...scenes, newScene]);
  };

  const handleRemoveScene = (index: number) => {
    const updated = scenes
      .filter((_, i) => i !== index)
      .map((s, idx) => ({ ...s, scene_order: idx + 1 }));
    onChange(updated);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Roteiro & Validação Cena a Cena</h4>
          <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.825rem', color: 'var(--text-secondary, #64748b)' }}>
            Gere e aprove uma cena por vez para economizar créditos e garantir continuidade total antes de prosseguir.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className={`vstudio-badge ${totalDuration === targetDuration ? 'vstudio-badge-success' : 'vstudio-badge-warning'}`}>
            ⏱ Total: {totalDuration}s / Alvo: {targetDuration}s
          </span>
          {onStitchScenes && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <select
                className="vstudio-select"
                value={transitionType}
                onChange={(e) => setTransitionType(e.target.value as StitchTransitionType)}
                disabled={isStitching}
                style={{ padding: '0.35rem 0.55rem', fontSize: '0.76rem', width: 'auto' }}
                title="Escolha o efeito de transição visual e de áudio entre as cenas"
              >
                <option value="crossfade">✨ Transição: Dissolve Suave (0.5s)</option>
                <option value="zoom_dissolve">🔍 Transição: Zoom + Dissolve</option>
                <option value="dip_to_black">🌑 Transição: Fade Escuro</option>
                <option value="cut">✂️ Transição: Corte c/ Áudio Suavizado</option>
              </select>
              <button
                type="button"
                className="vstudio-btn"
                style={{
                  background: readyScenesCount >= 2 ? 'linear-gradient(135deg, #10b981, #059669)' : '#334155',
                  color: readyScenesCount >= 2 ? '#fff' : '#94a3b8',
                  padding: '0.4rem 0.85rem',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  border: 'none',
                  cursor: (isStitching || readyScenesCount < 2) ? 'not-allowed' : 'pointer',
                  opacity: readyScenesCount >= 2 ? 1 : 0.65,
                }}
                disabled={isStitching || readyScenesCount < 2}
                onClick={() => onStitchScenes(transitionType)}
                title={readyScenesCount >= 2
                  ? "Junta os takes de todas as cenas prontas em um só vídeo contínuo com transição suave"
                  : `Envie ou aprove vídeo em pelo menos 2 cenas (${readyScenesCount}/${scenes.length} prontas) para juntar`}
              >
                {isStitching ? (stitchProgressMessage || '⏳ Unindo Cenas...') : `🎞️ Juntar Cenas (${readyScenesCount}/${scenes.length})`}
              </button>
            </div>
          )}
          <button type="button" className="vstudio-btn vstudio-btn-primary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }} onClick={handleAddScene}>
            + Adicionar Cena
          </button>
        </div>
      </div>

      {stitchProgressMessage && (
        <div style={{ marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: '0.5rem', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>🔄</span>
          <strong>{stitchProgressMessage}</strong>
        </div>
      )}

      {scenes.map((scene, idx) => {
        const isGenerating = generatingSceneOrder === scene.scene_order;
        const isUploading = uploadingSceneOrder === scene.scene_order;
        const prevScene = idx > 0 ? scenes[idx - 1] : null;
        const isPrevApproved = !prevScene || prevScene.status === 'approved';
        const isApproved = scene.status === 'approved';
        const sceneGenerations = getSceneGenerations(
          versions,
          scene.scene_order,
          scene.video_url,
          scene.duration_seconds
        );
        const activeVideoUrl = scene.video_url || sceneGenerations[0]?.video_url || null;
        const hasVideo = Boolean(activeVideoUrl);
        const wordCount = countDialogueWords(scene.dialogue);
        const autoDuration = calculateDialogueDurationSeconds(scene.dialogue, scene.duration_seconds || 5);

        return (
          <div
            key={scene.id || idx}
            className="vstudio-scene-item"
            style={{
              borderColor: isApproved ? 'rgba(34, 197, 94, 0.45)' : (hasVideo ? 'rgba(99, 102, 241, 0.45)' : undefined),
              background: isApproved ? 'rgba(34, 197, 94, 0.02)' : undefined,
            }}
          >
            <div className="vstudio-scene-header">
              <span style={{ fontWeight: 700, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span className="vstudio-badge vstudio-badge-primary">Cena #{scene.scene_order}</span>
                <span style={{ color: 'var(--text-primary, #f8fafc)' }}>
                  {idx === 0 ? '🎬 Abertura & Gancho (Hook)' : idx === 1 ? '💡 Demonstração & Solução (Body)' : idx === 2 ? '🎯 Desfecho & Chamada para Ação (CTA)' : `Take #${idx + 1}`}
                </span>
                {isApproved && <span className="vstudio-badge vstudio-badge-success">✓ Aprovada</span>}
                {!isApproved && hasVideo && <span className="vstudio-badge" style={{ background: '#e0e7ff', color: '#4338ca' }}>🎥 Gerada</span>}
                {!isApproved && !hasVideo && <span className="vstudio-badge vstudio-badge-warning">🟡 Pendente</span>}
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }} title={`Calculado automaticamente pela fala (${wordCount} palavras → ${autoDuration}s)`}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #64748b)' }}>Duração (Auto):</span>
                  <input
                    type="number"
                    min="2"
                    max="60"
                    className="vstudio-input"
                    style={{ width: '65px', padding: '0.25rem 0.4rem', textAlign: 'center', fontWeight: 700 }}
                    value={scene.duration_seconds}
                    onChange={(e) => handleUpdate(idx, { duration_seconds: Number(e.target.value) || 1 })}
                  />
                  <span style={{ fontSize: '0.8rem' }}>s</span>
                  {wordCount > 0 && scene.duration_seconds !== autoDuration && (
                    <button
                      type="button"
                      onClick={() => handleUpdate(idx, { duration_seconds: autoDuration })}
                      style={{
                        background: 'rgba(59, 130, 246, 0.15)',
                        color: '#60a5fa',
                        border: '1px solid rgba(59, 130, 246, 0.35)',
                        borderRadius: '0.3rem',
                        padding: '0.15rem 0.4rem',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      title="Sincronizar duração com o cálculo automático da fala"
                    >
                      ⚡ Ajustar p/ {autoDuration}s
                    </button>
                  )}
                </div>
                {scenes.length > 1 && (
                  <button type="button" className="vstudio-btn vstudio-btn-outline vstudio-btn-danger" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleRemoveScene(idx)}>
                    Excluir
                  </button>
                )}
              </div>
            </div>

            {hasVideo && (
              <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', overflow: 'hidden', background: '#0f172a', padding: '0.6rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem', padding: '0 0.2rem', flexWrap: 'wrap', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 600 }}>
                      🎬 Prévia do Take #{scene.scene_order}
                    </span>
                    {sceneGenerations.length > 0 && (
                      <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem', borderRadius: '999px', background: 'rgba(99, 102, 241, 0.18)', color: '#a5b4fc', fontWeight: 600 }}>
                        {sceneGenerations.length} {sceneGenerations.length === 1 ? 'geração disponível' : 'gerações disponíveis'}
                      </span>
                    )}
                  </div>
                  <a href={activeVideoUrl || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: '0.75rem' }}>
                    Abrir em nova aba ↗
                  </a>
                </div>

                {sceneGenerations.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem', padding: '0.35rem 0.45rem', background: 'rgba(15, 23, 42, 0.75)', borderRadius: '0.4rem', border: '1px solid rgba(148, 163, 184, 0.15)' }}>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, marginRight: '0.15rem' }}>
                      Escolher geração:
                    </span>
                    {sceneGenerations.map((gen) => {
                      const isCurrentTake = gen.video_url === activeVideoUrl;
                      const ts = formatVersionTimestamp(gen.created_at);
                      const dur = gen.duration_seconds || scene.duration_seconds;
                      return (
                        <button
                          key={gen.id}
                          type="button"
                          onClick={() => {
                            handleUpdate(idx, { video_url: gen.video_url, status: 'approved' });
                            if (onSelectSceneGeneration) {
                              onSelectSceneGeneration(scene.scene_order, gen.video_url, gen.duration_seconds);
                            }
                          }}
                          style={{
                            background: isCurrentTake
                              ? 'linear-gradient(135deg, #2563eb, #4f46e5)'
                              : 'rgba(30, 41, 59, 0.9)',
                            color: isCurrentTake ? '#ffffff' : '#cbd5e1',
                            border: isCurrentTake
                              ? '1px solid #60a5fa'
                              : '1px solid rgba(148, 163, 184, 0.25)',
                            borderRadius: '0.35rem',
                            padding: '0.22rem 0.55rem',
                            fontSize: '0.73rem',
                            fontWeight: isCurrentTake ? 700 : 500,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.15s ease',
                          }}
                          title={gen.review_notes || `Take v${gen.generationNumber} da Cena #${scene.scene_order}`}
                        >
                          <span>{isCurrentTake ? '✓' : '🎞️'} Take v{gen.generationNumber}</span>
                          {dur ? <span style={{ opacity: 0.85 }}>({dur}s)</span> : null}
                          {ts ? <span style={{ opacity: 0.7, fontSize: '0.68rem' }}>• {ts}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                )}

                <video
                  key={activeVideoUrl || undefined}
                  src={activeVideoUrl || undefined}
                  controls
                  preload="metadata"
                  playsInline
                  style={{ width: '100%', maxHeight: '200px', borderRadius: '0.35rem' }}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label className="vstudio-form-label">Cenário / Ambientação</label>
                <textarea
                  rows={2}
                  className="vstudio-textarea"
                  value={scene.scenario || ''}
                  onChange={(e) => handleUpdate(idx, { scenario: e.target.value })}
                  placeholder="Ex: Escritório executivo moderno, iluminação dramática"
                />
              </div>
              <div>
                <label className="vstudio-form-label">Ação / Enquadramento do Personagem</label>
                <textarea
                  rows={2}
                  className="vstudio-textarea"
                  value={scene.action || ''}
                  onChange={(e) => handleUpdate(idx, { action: e.target.value })}
                  placeholder="O que acontece em cena"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label className="vstudio-form-label">Movimento de Câmera</label>
                <input
                  type="text"
                  className="vstudio-input"
                  value={scene.camera || ''}
                  onChange={(e) => handleUpdate(idx, { camera: e.target.value })}
                  placeholder="Ex: Plano médio vertical com slow push-in"
                />
              </div>
              <div>
                <label className="vstudio-form-label">Iluminação & Fotografia</label>
                <input
                  type="text"
                  className="vstudio-input"
                  value={scene.lighting || ''}
                  onChange={(e) => handleUpdate(idx, { lighting: e.target.value })}
                  placeholder="Ex: Luz cinematográfica 4K, contraste suave"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem', flexWrap: 'wrap', gap: '0.35rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                    <label className="vstudio-form-label" style={{ margin: 0 }}>Fala / Locução (Voiceover)</label>
                    <span
                      style={{
                        fontSize: '0.72rem',
                        padding: '0.1rem 0.45rem',
                        borderRadius: '999px',
                        background: autoDuration > 8 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                        color: autoDuration > 8 ? '#fbbf24' : '#34d399',
                        fontWeight: 600,
                      }}
                      title="Tempo calculado automaticamente a partir do número de palavras e caracteres da fala"
                    >
                      ⏱ {wordCount} {wordCount === 1 ? 'palavra' : 'palavras'} • {autoDuration}s
                    </span>
                  </div>
                  {onNarrateScene && (
                    <button
                      type="button"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#3b82f6',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: narratingSceneOrder === scene.scene_order ? 'wait' : 'pointer',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '0.3rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                      disabled={narratingSceneOrder === scene.scene_order || !scene.dialogue?.trim()}
                      onClick={() => onNarrateScene(scene.scene_order)}
                      title="Sintetizar locução em português para esta fala via Google/OpenAI TTS"
                    >
                      {narratingSceneOrder === scene.scene_order ? '⏳ Sintetizando...' : '🎙️ Sintetizar Voz'}
                    </button>
                  )}
                </div>
                <textarea
                  rows={3}
                  className="vstudio-textarea"
                  style={{ resize: 'vertical', minHeight: '4.5rem', lineHeight: '1.45' }}
                  value={scene.dialogue || ''}
                  onChange={(e) => {
                    const newDialogue = e.target.value;
                    const newDuration = calculateDialogueDurationSeconds(newDialogue, 5);
                    handleUpdate(idx, {
                      dialogue: newDialogue,
                      duration_seconds: newDuration,
                    });
                  }}
                  placeholder="Texto narrado ou legenda falada (a duração da cena é recalculada automaticamente ao digitar)"
                />
                {autoDuration > 8 && (
                  <div style={{ marginTop: '0.35rem', fontSize: '0.73rem', color: '#fbbf24', lineHeight: '1.35' }}>
                    💡 Falas acima de 8s (~20 palavras) excedem o limite de um take único do Veo 3 (8s). A locução integral de {autoDuration}s será sintetizada no áudio da cena — ou reduza o texto para até ~20 palavras para lip-sync integral em 1 take.
                  </div>
                )}
                {scene.narration_audio_url && (
                  <div style={{ marginTop: '0.45rem', padding: '0.35rem 0.6rem', background: 'rgba(37, 99, 235, 0.08)', borderRadius: '0.4rem', border: '1px solid rgba(37, 99, 235, 0.25)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
                      🔊 Locução:
                    </span>
                    <audio controls src={scene.narration_audio_url} style={{ height: '26px', flex: 1, minWidth: '160px' }} />
                  </div>
                )}
              </div>
              <div>
                <label className="vstudio-form-label">Prompt Visual Recomendado (Para IA de Vídeo)</label>
                <textarea
                  rows={3}
                  className="vstudio-textarea"
                  style={{ resize: 'vertical', minHeight: '4.5rem', lineHeight: '1.45' }}
                  value={scene.visual_prompt || ''}
                  onChange={(e) => handleUpdate(idx, { visual_prompt: e.target.value })}
                  placeholder="Prompt em inglês para Runway/Kling/Flow"
                />
              </div>
            </div>

            {!isPrevApproved && !hasVideo && (
              <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '0.4rem', background: '#fef3c7', color: '#92400e', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span>🛡️</span>
                <span><strong>Proteção de Créditos:</strong> Valide e aprove a <strong>Cena #{prevScene?.scene_order}</strong> antes de processar esta cena.</span>
              </div>
            )}

            <div style={{ marginTop: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {onApproveScene && (
                  <button
                    type="button"
                    className={`vstudio-btn ${isApproved ? 'vstudio-btn-outline' : 'vstudio-btn-success'}`}
                    style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem' }}
                    onClick={() => onApproveScene(scene.scene_order, !isApproved)}
                    title={isApproved ? "Clique para desmarcar aprovação" : "Validar e aprovar esta cena para liberar o fluxo"}
                  >
                    {isApproved ? '↩ Desmarcar Aprovação' : `✓ Validar & Aprovar Cena #${scene.scene_order}`}
                  </button>
                )}

                {onUploadSceneVideo && (
                  <label
                    htmlFor={`scene-video-upload-${scene.scene_order}`}
                    className="vstudio-btn"
                    style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.6 : 1, background: 'linear-gradient(135deg, #0284c7, #0369a1)', color: '#ffffff', fontWeight: 600, border: 'none', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.25)', margin: 0 }}
                    title="Subir arquivo de vídeo pronto (.mp4, .webm, .mov) para esta cena"
                  >
                    <input
                      id={`scene-video-upload-${scene.scene_order}`}
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm"
                      style={{ display: 'none' }}
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          onUploadSceneVideo(scene.scene_order, file);
                          e.target.value = '';
                        }
                      }}
                    />
                    {isUploading ? '⏳ Enviando...' : '📤 Subir Vídeo Pronto'}
                  </label>
                )}

                {onOpenTrimmer && (
                  <button
                    type="button"
                    className="vstudio-btn"
                    style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#ffffff', fontWeight: 600, border: 'none', boxShadow: '0 2px 6px rgba(139, 92, 246, 0.25)', cursor: 'pointer' }}
                    onClick={() => onOpenTrimmer(scene)}
                    title="Abrir editor de corte para selecionar início e fim de um vídeo"
                  >
                    ✂️ Recortar Trecho
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {onGenerateScene && (
                  <button
                    type="button"
                    className="vstudio-btn vstudio-btn-primary"
                    style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', opacity: (!isPrevApproved && !hasVideo) ? 0.75 : 1 }}
                    disabled={isGenerating || (generatingSceneOrder !== null && generatingSceneOrder !== undefined) || isUploading}
                    onClick={() => onGenerateScene(scene.scene_order)}
                    title={`Renderiza apenas o take da Cena #${scene.scene_order} no Google Veo 3.1 com continuidade`}
                  >
                    {isGenerating ? <>⏳ Renderizando Cena #{scene.scene_order}...</> : hasVideo ? <>🔄 Re-renderizar</> : <>⚡ Processar Cena #{scene.scene_order}</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {onStitchScenes && (
        <div style={{
          marginTop: '1.25rem',
          padding: '1rem',
          background: readyScenesCount >= 2 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(30, 41, 59, 0.5)',
          borderRadius: '0.65rem',
          border: readyScenesCount >= 2 ? '1px solid rgba(16, 185, 129, 0.4)' : '1px dashed rgba(100, 116, 139, 0.4)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: readyScenesCount >= 2 ? '#10b981' : '#f8fafc' }}>
              🎬 Conclusão da Produção & Junção
            </span>
            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
              {readyScenesCount >= 2
                ? `Mescle todas as ${readyScenesCount} cenas prontas em um só vídeo contínuo e oficial para a campanha.`
                : `Envie ou recorte o vídeo em pelo menos 2 cenas (${readyScenesCount}/${scenes.length} prontas) para liberar a junção em 1 vídeo.`}
            </p>
          </div>
          <button
            type="button"
            className="vstudio-btn"
            style={{
              background: readyScenesCount >= 2 ? 'linear-gradient(135deg, #10b981, #059669)' : '#334155',
              color: readyScenesCount >= 2 ? '#fff' : '#94a3b8',
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: 700,
              border: 'none',
              cursor: (isStitching || readyScenesCount < 2) ? 'not-allowed' : 'pointer',
              opacity: readyScenesCount >= 2 ? 1 : 0.65,
            }}
            disabled={isStitching || readyScenesCount < 2}
            onClick={() => onStitchScenes(transitionType)}
            title={readyScenesCount >= 2 ? "Mesclar cenas em um só vídeo" : "Necessário pelo menos 2 cenas com vídeo pronto"}
          >
            {isStitching ? (stitchProgressMessage || '⏳ Unindo Cenas...') : `🎞️ Juntar Todas as Cenas em 1 Vídeo (${readyScenesCount}/${scenes.length})`}
          </button>
        </div>
      )}
    </div>
  );
};
